/**
 * Card collection editing.
 *
 * One collection element holds N cards, so this panel edits two levels: the
 * collection (columns and shared surface treatment) and the selected card
 * (its own content). Splitting them matters — surface, shadow and hover are
 * properties of the set, not of one card, and offering them per card would
 * let a user produce a row where each card looks like it came from a
 * different site.
 */
import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Copy, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { ContentBlock, SurfaceEffects } from '../types';
import { Segmented, TextField } from './ChromeInspector';
import { IconPicker } from './IconPicker';

type CardItem = NonNullable<ContentBlock['items']>[number];

export function CardInspector({
  block,
  onPatch,
}: {
  block: ContentBlock;
  onPatch: (patch: Partial<ContentBlock>) => void;
}) {
  const items = block.items ?? [];
  const effects: SurfaceEffects = block.effects ?? { shadow: 'subtle', border: 'none', radius: 'rounded', hover: 'lift' };
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = items[selectedIndex] ?? null;

  const setItems = (next: CardItem[]) => onPatch({ items: next });
  const setEffects = (patch: Partial<SurfaceEffects>) => onPatch({ effects: { ...effects, ...patch } });

  const patchCard = (index: number, patch: Partial<CardItem>) =>
    setItems(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    setItems(next);
    setSelectedIndex(target);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-bold text-slate-900">Kaarten</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          Op mobiel staan kaarten altijd onder elkaar.
        </p>
      </div>

      <Segmented
        label="Kolommen"
        value={String(Math.min(Math.max(items.length, 1), 4))}
        options={[['1', '1'], ['2', '2'], ['3', '3'], ['4', '4']]}
        onChange={(value) => {
          // The column count is the card count: a "4 columns" row with three
          // cards would render a gap, and a fourth empty card is a clearer
          // starting point than an invisible hole.
          const target = Number(value);
          if (target > items.length) {
            setItems([...items, ...Array.from({ length: target - items.length }, () => emptyCard())]);
          } else if (target < items.length) {
            setItems(items.slice(0, target));
            setSelectedIndex((index) => Math.min(index, target - 1));
          }
        }}
      />

      <div>
        <span className="block text-sm font-semibold text-slate-800">Kaart bewerken</span>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setSelectedIndex(index)}
              className={cn(
                'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition',
                index === selectedIndex
                  ? 'border-teal-400 bg-teal-50 text-teal-800'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50',
              )}
            >
              {item.title || `Kaart ${index + 1}`}
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setItems([...items, emptyCard()]); setSelectedIndex(items.length); }}
            className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
          >
            <Plus className="h-3.5 w-3.5" /> Kaart
          </button>
        </div>
      </div>

      {selected ? (
        <div className="space-y-3 rounded-xl border border-teal-200 bg-teal-50/30 p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-slate-900">Kaart {selectedIndex + 1}</h4>
            <div className="flex gap-1">
              <Mini label="Naar links" onClick={() => move(selectedIndex, -1)}><ArrowLeft className="h-3.5 w-3.5" /></Mini>
              <Mini label="Naar rechts" onClick={() => move(selectedIndex, 1)}><ArrowRight className="h-3.5 w-3.5" /></Mini>
              <Mini label="Dupliceren" onClick={() => { const next = items.slice(); next.splice(selectedIndex + 1, 0, { ...selected }); setItems(next); setSelectedIndex(selectedIndex + 1); }}>
                <Copy className="h-3.5 w-3.5" />
              </Mini>
              <Mini
                label="Verwijderen"
                destructive
                onClick={() => { setItems(items.filter((_, i) => i !== selectedIndex)); setSelectedIndex((i) => Math.max(0, i - 1)); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Mini>
            </div>
          </div>

          <TextField label="Kop" value={selected.title} onChange={(title) => patchCard(selectedIndex, { title })} />
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700">Tekst</span>
            <textarea
              rows={3}
              value={selected.text}
              onChange={(event) => patchCard(selectedIndex, { text: event.target.value })}
              className="mt-1.5 w-full resize-y rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
            />
          </label>
          <label className="block text-xs font-semibold text-slate-700">Tekstgrootte (px)
            <input type="number" min={10} max={72} value={selected.textFontSize ?? ''} onChange={(event) => patchCard(selectedIndex, { textFontSize: event.target.value ? Number(event.target.value) : undefined })} placeholder="Site standaard" className="mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-teal-500" />
          </label>
          <IconPicker value={selected.icon ?? 'none'} onChange={(icon) => patchCard(selectedIndex, { icon })} />
          {selected.icon && selected.icon !== 'none' ? (
            <Segmented
              label="Icoonpositie"
              value={selected.iconPosition ?? 'top'}
              options={[['top', 'Boven'], ['left', 'Links'], ['right', 'Rechts']]}
              onChange={(value) => patchCard(selectedIndex, { iconPosition: value as CardItem['iconPosition'] })}
            />
          ) : null}
          <TextField
            label="Afbeelding (mediaverwijzing)"
            value={selected.assetId ?? ''}
            onChange={(assetId) => patchCard(selectedIndex, { assetId: assetId || null })}
            placeholder="Optioneel"
          />
        </div>
      ) : null}

      <div className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Stijl van alle kaarten</h4>
        <Segmented
          label="Schaduw"
          value={effects.shadow ?? 'none'}
          options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['medium', 'Medium'], ['strong', 'Sterk']]}
          onChange={(value) => setEffects({ shadow: value as SurfaceEffects['shadow'] })}
        />
        <Segmented
          label="Rand"
          value={effects.border ?? 'none'}
          options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['accent', 'Accent'], ['strong', 'Sterk']]}
          onChange={(value) => setEffects({ border: value as SurfaceEffects['border'] })}
        />
        <Segmented
          label="Afronding"
          value={effects.radius ?? 'subtle'}
          options={[['sharp', 'Recht'], ['subtle', 'Subtiel'], ['rounded', 'Rond'], ['soft', 'Zacht']]}
          onChange={(value) => setEffects({ radius: value as SurfaceEffects['radius'] })}
        />
        <Segmented
          label="Bij aanwijzen"
          value={effects.hover ?? 'none'}
          options={[['none', 'Geen'], ['lift', 'Optillen'], ['shadow', 'Schaduw'], ['accent', 'Accent'], ['scale', 'Vergroten']]}
          onChange={(value) => setEffects({ hover: value as SurfaceEffects['hover'] })}
        />
      </div>
    </div>
  );
}

function emptyCard(): CardItem {
  return { title: 'Nieuwe kaart', text: 'Korte toelichting.', icon: 'none' };
}

function Mini({
  label,
  onClick,
  destructive,
  children,
}: {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition',
        destructive ? 'hover:border-red-200 hover:bg-red-50 hover:text-red-600' : 'hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700',
      )}
    >
      {children}
    </button>
  );
}
